import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  API_BRANCHES,
  API_CENTERS,
  API_GET_REPAYMENT_REPORT,
} from '../Config/ApiUrlConstant';
import { useAuth } from '../AuthContext';
import * as XLSX from 'xlsx';

const RepaymentReport = () => {
  const [reportData, setReportData] = useState([]);
  const [centerList, setCenterList] = useState([]);
  const [branches, setBranches] = useState([]);
  const [formData, setFormData] = useState({
    branch_id: '',
    center_id: '',
    from_date: '',
    to_date: '',
  });
  const [filters, setFilters] = useState({
    branch_no: '',
    branch_name: '',
    center_no: '',
    center_name: '',
    client_no: '',
    client_name: '',
  });
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const token = localStorage.getItem('token');
  const { logout, selectedBranchId } = useAuth();

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const response = await fetch(API_BRANCHES, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        });
        if (response.status === 401 || response.status === 403) {
          logout();
          return;
        }
        const data = await response.json();
        setBranches(data.data || []);
      } catch (error) {
        setBranches([]);
      }
    };

    void loadBranches();
  }, [logout, token]);

  const getCentersByBranch = async (branchId) => {
    try {
      const response = await fetch(API_CENTERS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_all: false, branch_id: branchId }),
      });
      if (response.status === 401 || response.status === 403) {
        logout();
        return;
      }
      const data = await response.json();
      setCenterList(data.data || []);
    } catch (error) {
      setCenterList([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'branch_id') {
      if (!value) {
        setCenterList([]);
        setFormData((prevData) => ({ ...prevData, branch_id: '', center_id: '' }));
        return;
      }

      void getCentersByBranch(value);
      setFormData((prevData) => ({ ...prevData, branch_id: value, center_id: '' }));
      return;
    }

    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);

    const filtered = reportData.filter((row) =>
      Object.entries(newFilters).every(([col, filterValue]) =>
        row[col]?.toString().toLowerCase().includes(filterValue.toLowerCase())
      )
    );
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const getRepaymentReport = async () => {
    try {
      const response = await fetch(API_GET_REPAYMENT_REPORT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...formData, branch_id: formData.branch_id || selectedBranchId || '' }),
      });
      if (response.status === 401 || response.status === 403) {
        logout();
        return;
      }
      const data = await response.json();
      setReportData(data.data || []);
      setFilteredData(data.data || []);
    } catch (error) {
    }
  };

  const exportToExcel = () => {
    const headers = [
      'Branch No', 'Branch Name', 'Center No', 'Center Name',
      'Client No', 'Client Name', 'Installment Amt', 'Principal Amt', 'Interest Amt',
    ];

    const dataToExport = filteredData.map((row) => [
      row.branch_no,
      row.branch_name,
      row.center_no,
      row.center_name,
      row.client_no,
      row.client_name,
      row.total_installment_amt,
      row.total_installment_principal_amt,
      row.total_installment_interest_amt,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...dataToExport]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Repayment Report');
    XLSX.writeFile(workbook, 'RepaymentReport.xlsx');
  };

  const totalRows = filteredData.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const pageStart = (currentPage - 1) * rowsPerPage;
  const pageData = filteredData.slice(pageStart, pageStart + rowsPerPage);
  const totalInstallmentAmt = filteredData.reduce((acc, row) => acc + parseFloat(row.total_installment_amt || 0), 0);
  const totalPrincipalAmt = filteredData.reduce((acc, row) => acc + parseFloat(row.total_installment_principal_amt || 0), 0);
  const totalInterestAmt = filteredData.reduce((acc, row) => acc + parseFloat(row.total_installment_interest_amt || 0), 0);

  return (
    <div className="container py-4">
      <div className="row g-3 mb-3">
        <div className="col-md-3">
          <label>Branch Name</label>
          <select name="branch_id" value={formData.branch_id} onChange={handleChange} className="form-control">
            <option value="">Select Branch</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.branch_name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <label>Center Name</label>
          <select name="center_id" value={formData.center_id} onChange={handleChange} className="form-control">
            <option value="">Select Center</option>
            {centerList.map((center) => (
              <option key={center.id} value={center.id}>
                {center.center_name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <label>From Date</label>
          <input type="date" name="from_date" className="form-control" value={formData.from_date} onChange={handleChange} />
        </div>
        <div className="col-md-3">
          <label>To Date</label>
          <input type="date" name="to_date" className="form-control" value={formData.to_date} onChange={handleChange} />
        </div>
        <div className="col-12 col-md-2 mt-3 d-grid">
          <button type="button" className="btn btn-primary mt-2" onClick={getRepaymentReport}>
            Submit
          </button>
        </div>
      </div>
      <div className="col-12 col-md-2 mt-3 d-grid">
        <button type="button" className="btn btn-success mt-2" onClick={exportToExcel}>
          Export to Excel
        </button>
      </div>

      <div className="table-responsive data-table-shell">
        <table className="table table-bordered table-striped data-table">
          <thead>
            <tr>
              {['branch_no', 'branch_name', 'center_no', 'center_name', 'client_no', 'client_name'].map((col) => (
                <th key={col}>{col.replace('_', ' ').toUpperCase()}</th>
              ))}
              <th>Installment Amt</th>
              <th>Principal Amt</th>
              <th>Interest Amt</th>
            </tr>
            <tr>
              {['branch_no', 'branch_name', 'center_no', 'center_name', 'client_no', 'client_name'].map((col) => (
                <th key={col}>
                  <input
                    type="text"
                    name={col}
                    value={filters[col]}
                    onChange={handleFilterChange}
                    className="form-control form-control-sm"
                    placeholder="Filter"
                  />
                </th>
              ))}
              <th></th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pageData.map((row, idx) => (
              <tr key={idx}>
                <td>{row.branch_no}</td>
                <td>{row.branch_name}</td>
                <td>{row.center_no}</td>
                <td>{row.center_name}</td>
                <td>{row.client_no}</td>
                <td>{row.client_name}</td>
                <td>{row.total_installment_amt}</td>
                <td>{row.total_installment_principal_amt}</td>
                <td>{row.total_installment_interest_amt}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="table-secondary fw-bold">
              <td colSpan="6" className="text-end">Total:</td>
              <td>{totalInstallmentAmt.toFixed(2)}</td>
              <td>{totalPrincipalAmt.toFixed(2)}</td>
              <td>{totalInterestAmt.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center">
        <div>Total Rows: {totalRows}</div>
        <nav>
          <ul className="pagination mb-0">
            {Array.from({ length: totalPages }, (_, i) => (
              <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                  {i + 1}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default RepaymentReport;
